import { useQueryClient } from '@tanstack/react-query';
import type {
  EntitlementUsageDto,
  ScheduledPlanChangeDto,
} from '@up2stay/up2promo-api-client';
import {
  type SubscriptionStatus,
  type WorkspaceEntitlementsDto,
  type WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { useCallback, useMemo } from 'react';

import type { FeatureKey, QuotaKey } from '@/core/authz/entitlement-catalog';
import {
  useWorkspaceEntitlements,
  workspaceEntitlementsQueryKey,
} from '@/core/hooks/use-workspace-entitlements';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type { FeatureKey, QuotaKey };

export type WorkspaceEntitlementsWithGrant = WorkspaceEntitlementsDto & {
  billingGrant?: {
    plan: string;
    memberLimit: number | null;
    reputationLocationLimit: number | null;
    reason: string;
    grantedAt: string;
    expiresAt: string | null;
  } | null;
  hasActiveBillingGrant?: boolean;
};

interface UseEntitlementsResult {
  plan: WorkspacePlan | undefined;
  status: SubscriptionStatus | undefined;
  seats: number | undefined;
  reputationLocations: number | undefined;
  features: Set<FeatureKey>;
  isFeatureEnabled: (feature: FeatureKey) => boolean;
  getQuota: (quota: QuotaKey) => number | undefined;
  usage: EntitlementUsageDto | undefined;
  scheduledChange: ScheduledPlanChangeDto | null | undefined;
  billingGrant: WorkspaceEntitlementsWithGrant['billingGrant'];
  hasActiveBillingGrant: boolean;
  isLoading: boolean;
}

/**
 * Resolves the current workspace's plan entitlements from the backend.
 * Reactive to workspace switch; use for all plan-gating decisions in the UI.
 */
export function useEntitlements(): UseEntitlementsResult {
  const currentWorkspace = useCurrentWorkspace();
  const query = useWorkspaceEntitlements(currentWorkspace?.id);
  const data = query.data as WorkspaceEntitlementsWithGrant | null | undefined;

  const features = useMemo(() => {
    const enabled = new Set<FeatureKey>();
    if (!data?.features) return enabled;
    for (const [key, value] of Object.entries(data.features)) {
      if (value) enabled.add(key as FeatureKey);
    }
    return enabled;
  }, [data]);

  const isLoading = !currentWorkspace || query.isPending;

  const isFeatureEnabled = useCallback(
    (feature: FeatureKey) => {
      if (isLoading) return false;
      return data?.features?.[feature] === true;
    },
    [data, isLoading],
  );

  const getQuota = useCallback(
    (quota: QuotaKey) => data?.quotas?.[quota],
    [data],
  );

  return {
    plan: data?.plan,
    status: data?.status ?? undefined,
    seats: data?.seats,
    reputationLocations: data?.reputationLocations,
    features,
    isFeatureEnabled,
    getQuota,
    usage: data?.usage,
    scheduledChange: data?.scheduledChange,
    billingGrant: data?.billingGrant ?? null,
    hasActiveBillingGrant: data?.hasActiveBillingGrant === true,
    isLoading,
  };
}

export function useInvalidateWorkspaceEntitlements() {
  const queryClient = useQueryClient();

  return useCallback(
    (workspaceId?: string) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceEntitlementsQueryKey(workspaceId),
      });
    },
    [queryClient],
  );
}
